import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './AdminCultureMedia.css';

const AdminCultureMedia = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [culture, setCulture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery');
  
  // Gallery state
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryData, setGalleryData] = useState([]);
  
  // Reference state
  const [referenceForm, setReferenceForm] = useState({
    title: '',
    author: '',
    publication: '',
    year: '',
    url: '',
    type: 'article'
  });

  useEffect(() => {
    fetchCulture();
    // eslint-disable-next-line
  }, [id]);

  const fetchCulture = async () => {
    try {
      const response = await fetch(`http://localhost:5000/api/cultures/${id}`);
      if (!response.ok) throw new Error('Culture not found');
      const data = await response.json();
      setCulture(data);
      setLoading(false);
    } catch (err) {
      alert('Failed to load culture');
      navigate('/admin/dashboard/cultures');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setGalleryFiles(files);
    
    // Initialize metadata for each file
    const newData = files.map((file) => ({
      file,
      title: file.name.replace(/\.[^/.]+$/, ''),
      description: '',
      category: 'art',
      credit: '',
      source: ''
    }));
    setGalleryData(newData);
  };

  const updateGalleryItem = (index, field, value) => {
    const updated = [...galleryData];
    updated[index][field] = value;
    setGalleryData(updated);
  };

  const handleGalleryUpload = async () => {
    if (galleryFiles.length === 0) {
      alert('Please select images to upload');
      return;
    }

    const formData = new FormData();
    galleryData.forEach((item, index) => {
      formData.append('images', item.file);
      formData.append(`images[${index}][title]`, item.title);
      formData.append(`images[${index}][description]`, item.description);
      formData.append(`images[${index}][category]`, item.category);
      formData.append(`images[${index}][credit]`, item.credit);
      formData.append(`images[${index}][source]`, item.source);
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:5000/api/cultures/${culture._id}/gallery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');
      
      alert('Images uploaded successfully');
      setGalleryFiles([]);
      setGalleryData([]);
      fetchCulture();
    } catch (err) {
      alert('Failed to upload images: ' + err.message);
    }
  };

  const handleReferenceSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:5000/api/cultures/${culture._id}/references`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(referenceForm)
      });

      if (!response.ok) throw new Error('Failed to add reference');
      
      alert('Reference added successfully');
      setReferenceForm({
        title: '',
        author: '',
        publication: '',
        year: '',
        url: '',
        type: 'article'
      });
      fetchCulture();
    } catch (err) {
      alert('Failed to add reference: ' + err.message);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-culture-media">
      <div className="media-header">
        <h2>{culture.displayName} - Media & References</h2>
        <button onClick={() => navigate('/admin/dashboard/cultures')} className="btn-back">
          ← Back to Cultures
        </button>
      </div>

      <div className="media-tabs">
        <button
          className={activeTab === 'gallery' ? 'active' : ''}
          onClick={() => setActiveTab('gallery')}
        >
          Gallery ({culture.gallery?.length || 0})
        </button>
        <button
          className={activeTab === 'references' ? 'active' : ''}
          onClick={() => setActiveTab('references')}
        >
          References ({culture.references?.length || 0})
        </button>
      </div>

      {activeTab === 'gallery' && (
        <div className="gallery-section">
          <div className="upload-section">
            <h3>Upload New Images</h3>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="file-input"
            />

            {galleryData.length > 0 && (
              <div className="gallery-preview">
                {galleryData.map((item, index) => (
                  <div key={index} className="preview-item">
                    <img
                      src={URL.createObjectURL(item.file)}
                      alt={item.title}
                      className="preview-image"
                    />
                    <div className="preview-fields">
                      <input
                        type="text"
                        placeholder="Title"
                        value={item.title}
                        onChange={(e) => updateGalleryItem(index, 'title', e.target.value)}
                      />
                      <textarea
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateGalleryItem(index, 'description', e.target.value)}
                      />
                      <select
                        value={item.category}
                        onChange={(e) => updateGalleryItem(index, 'category', e.target.value)}
                      >
                        <option value="art">Art</option>
                        <option value="clothing">Clothing</option>
                        <option value="ceremony">Ceremony</option>
                        <option value="landscape">Landscape</option>
                        <option value="people">People</option>
                        <option value="food">Food</option>
                        <option value="crafts">Crafts</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Credit (photographer/artist)"
                        value={item.credit}
                        onChange={(e) => updateGalleryItem(index, 'credit', e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Source URL"
                        value={item.source}
                        onChange={(e) => updateGalleryItem(index, 'source', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
                <button onClick={handleGalleryUpload} className="btn btn-primary">
                  Upload All Images
                </button>
              </div>
            )}
          </div>

          <div className="existing-gallery">
            <h3>Existing Gallery Images</h3>
            {culture.gallery && culture.gallery.length > 0 ? (
              <div className="gallery-grid">
                {culture.gallery.map((item, index) => (
                  <div key={index} className="gallery-card">
                    <img
                      src={`http://localhost:5000${item.imageUrl}`}
                      alt={item.title}
                    />
                    <div className="gallery-info">
                      <h4>{item.title}</h4>
                      <p>{item.description}</p>
                      <span className="category">{item.category}</span>
                      {item.credit && <p className="credit">Credit: {item.credit}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">No images uploaded yet</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'references' && (
        <div className="references-section">
          <div className="add-reference">
            <h3>Add New Reference</h3>
            <form onSubmit={handleReferenceSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={referenceForm.title}
                  onChange={(e) => setReferenceForm({...referenceForm, title: e.target.value})}
                  placeholder="Title of the work"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Author</label>
                  <input
                    type="text"
                    value={referenceForm.author}
                    onChange={(e) => setReferenceForm({...referenceForm, author: e.target.value})}
                    placeholder="Author name"
                  />
                </div>

                <div className="form-group">
                  <label>Year</label>
                  <input
                    type="text"
                    value={referenceForm.year}
                    onChange={(e) => setReferenceForm({...referenceForm, year: e.target.value})}
                    placeholder="Publication year"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Publication</label>
                <input
                  type="text"
                  value={referenceForm.publication}
                  onChange={(e) => setReferenceForm({...referenceForm, publication: e.target.value})}
                  placeholder="Journal, book, website, etc."
                />
              </div>

              <div className="form-group">
                <label>URL</label>
                <input
                  type="url"
                  value={referenceForm.url}
                  onChange={(e) => setReferenceForm({...referenceForm, url: e.target.value})}
                  placeholder="https://example.com"
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  value={referenceForm.type}
                  onChange={(e) => setReferenceForm({...referenceForm, type: e.target.value})}
                >
                  <option value="article">Article</option>
                  <option value="book">Book</option>
                  <option value="academic">Academic Paper</option>
                  <option value="museum">Museum</option>
                  <option value="website">Website</option>
                  <option value="documentary">Documentary</option>
                  <option value="interview">Interview</option>
                </select>
              </div>

              <button type="submit" className="btn btn-primary">
                Add Reference
              </button>
            </form>
          </div>

          <div className="existing-references">
            <h3>Existing References</h3>
            {culture.references && culture.references.length > 0 ? (
              <div className="references-list">
                {culture.references.map((ref, index) => (
                  <div key={index} className="reference-card">
                    <div className="ref-number">{index + 1}</div>
                    <div className="ref-content">
                      <h4>{ref.title}</h4>
                      {ref.author && <p className="author">By {ref.author}</p>}
                      {ref.publication && <p>{ref.publication}</p>}
                      {ref.year && <p>Year: {ref.year}</p>}
                      <span className="ref-type">{ref.type}</span>
                      {ref.url && (
                        <a href={ref.url} target="_blank" rel="noopener noreferrer">
                          View Source →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">No references added yet</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCultureMedia;
